/**
 * Grade Room — v1 op math (THE PARITY CONTRACT).
 *
 * Mirrors backend/services/grade_recipe_compiler.py EXACTLY. Any change here
 * must land in the compiler (and the EC2 worker) in the same commit, or the
 * preview and the final render will drift.
 *
 * Serial node stack, RGB in [0,1], per node:
 *   1. cdl    — out = clamp01(in * slope + offset) ^ power
 *               slope = (1+gain)*(1+gain_master)
 *               offset = offset + offset_master + 0.5*(lift + lift_master)
 *               power = 1 / max(0.05, 1 + gamma + gamma_master)
 *   2. adjust — contrast around pivot, Rec.709 saturation, temp/tint
 *   3. curves — point curves baked to a 256-sample 1D LUT (linear interp;
 *               compiler bakes 1024 — same interpolation, ΔE < preview JPEG)
 *   4. lut_ref — 3D .cube applied at `intensity` (trilinear)
 *   ± qualifier — HSL soft mask; node output = mix(node input, graded, mask).
 *               Qualifier renders are BLOCKED server-side until the P3 worker
 *               ships; this JS math is the contract that worker must mirror.
 *
 * The WebGPU engine (webgpuEngine.js) implements the same formulas in WGSL.
 */

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

export const IDENTITY_CDL = () => ({
  lift: [0, 0, 0], gamma: [0, 0, 0], gain: [0, 0, 0], offset: [0, 0, 0],
  lift_master: 0, gamma_master: 0, gain_master: 0, offset_master: 0,
});

export const IDENTITY_ADJUST = () => ({
  contrast: 1, pivot: 0.435, saturation: 1, temp: 0, tint: 0,
});

export const IDENTITY_QUALIFIER = () => ({
  enabled: false,
  hue_min: 0, hue_max: 360, sat_min: 0, sat_max: 1, lum_min: 0, lum_max: 1,
  softness: 0.25,
});

export const newNode = (label) => ({
  id: `n_${Date.now()}_${Math.floor(Math.random() * 1e4)}`,
  label: label || "node",
  enabled: true,
  ops: { cdl: IDENTITY_CDL(), adjust: IDENTITY_ADJUST(), curves: {}, lut_ref: null },
});

/* ── curve baking — same linear interp as the compiler's _bake_curve_1d ── */
export function bakeCurve1D(points, size = 256) {
  if (!points || points.length < 2) return null;
  const pts = [...points]
    .map((p) => [clamp01(p.x), clamp01(p.y)])
    .sort((a, b) => a[0] - b[0]);
  const lut = new Float32Array(size);
  let j = 0;
  for (let i = 0; i < size; i++) {
    const x = i / (size - 1);
    while (j < pts.length - 2 && pts[j + 1][0] < x) j++;
    const [x0, y0] = pts[j], [x1, y1] = pts[j + 1];
    if (x <= x0) lut[i] = y0;
    else if (x >= x1) lut[i] = y1;
    else lut[i] = y0 + ((x - x0) / Math.max(1e-9, x1 - x0)) * (y1 - y0);
  }
  return lut;
}

const sampleLut1D = (lut, v) => {
  const f = clamp01(v) * (lut.length - 1);
  const i = Math.floor(f), t = f - i;
  return i >= lut.length - 1 ? lut[lut.length - 1] : lut[i] * (1 - t) + lut[i + 1] * t;
};

/* ── .cube 3D LUT parsing + trilinear sampling ─────────────────────────── */
export function parseCubeLUT(text) {
  let size = 0;
  const table = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith("#") || line.startsWith("TITLE")) continue;
    if (line.startsWith("LUT_3D_SIZE")) { size = parseInt(line.split(/\s+/)[1], 10); continue; }
    if (/^[A-Z]/.test(line)) continue; // DOMAIN_MIN etc — assume 0..1
    const parts = line.split(/\s+/).map(Number);
    if (parts.length >= 3 && parts.every((n) => !Number.isNaN(n))) {
      table.push(parts[0], parts[1], parts[2]);
    }
  }
  if (!size || table.length < size * size * size * 3) return null;
  return { size, data: new Float32Array(table) };
}

function sampleLut3D(lut, r, g, b, out) {
  const N = lut.size, d = lut.data, M = N - 1;
  const fr = clamp01(r) * M, fg = clamp01(g) * M, fb = clamp01(b) * M;
  const r0 = Math.floor(fr), g0 = Math.floor(fg), b0 = Math.floor(fb);
  const r1 = Math.min(r0 + 1, M), g1 = Math.min(g0 + 1, M), b1 = Math.min(b0 + 1, M);
  const tr = fr - r0, tg = fg - g0, tb = fb - b0;
  // .cube ordering: index = (b*N*N + g*N + r) — red fastest
  const idx = (rr, gg, bb) => ((bb * N + gg) * N + rr) * 3;
  for (let c = 0; c < 3; c++) {
    const c000 = d[idx(r0, g0, b0) + c], c100 = d[idx(r1, g0, b0) + c];
    const c010 = d[idx(r0, g1, b0) + c], c110 = d[idx(r1, g1, b0) + c];
    const c001 = d[idx(r0, g0, b1) + c], c101 = d[idx(r1, g0, b1) + c];
    const c011 = d[idx(r0, g1, b1) + c], c111 = d[idx(r1, g1, b1) + c];
    const c00 = c000 * (1 - tr) + c100 * tr, c10 = c010 * (1 - tr) + c110 * tr;
    const c01 = c001 * (1 - tr) + c101 * tr, c11 = c011 * (1 - tr) + c111 * tr;
    out[c] = (c00 * (1 - tg) + c10 * tg) * (1 - tb) + (c01 * (1 - tg) + c11 * tg) * tb;
  }
}

/* ── qualifier (HSL soft mask) — P3 worker contract ────────────────────── */
function rgb2hsl(r, g, b) {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  const l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const dd = mx - mn;
  const s = l > 0.5 ? dd / (2 - mx - mn) : dd / (mx + mn);
  let h;
  if (mx === r) h = ((g - b) / dd + (g < b ? 6 : 0));
  else if (mx === g) h = (b - r) / dd + 2;
  else h = (r - g) / dd + 4;
  return [h * 60, s, l];
}

function bandMask(v, lo, hi, feather) {
  if (v >= lo && v <= hi) return 1;
  if (v < lo - feather || v > hi + feather) return 0;
  return v < lo ? (v - (lo - feather)) / feather : ((hi + feather) - v) / feather;
}

function hueMask(h, lo, hi, feather) {
  if (lo <= hi) return bandMask(h, lo, hi, feather);
  // wrapped band (e.g. 320..40): inside if h>=lo OR h<=hi
  return Math.max(bandMask(h, lo, 360 + hi, feather),
    bandMask(h + 360, lo, 360 + hi, feather));
}

export function qualifierMask(q, r, g, b) {
  const [h, s, l] = rgb2hsl(r, g, b);
  const fh = Math.max(1e-4, q.softness * 30);       // hue feather in degrees
  const fs = Math.max(1e-4, q.softness * (q.sat_max - q.sat_min || 1) * 0.5);
  const fl = Math.max(1e-4, q.softness * (q.lum_max - q.lum_min || 1) * 0.5);
  return hueMask(h, q.hue_min, q.hue_max, fh)
    * bandMask(s, q.sat_min, q.sat_max, fs)
    * bandMask(l, q.lum_min, q.lum_max, fl);
}

/* ── node compilation (per-frame constants) ────────────────────────────── */
export function compileNode(node, lutCache) {
  const ops = node.ops || {};
  const cdl = ops.cdl || IDENTITY_CDL();
  const adj = { ...IDENTITY_ADJUST(), ...(ops.adjust || {}) };
  const slope = [], offset = [], power = [];
  for (let c = 0; c < 3; c++) {
    slope[c] = (1 + (cdl.gain?.[c] || 0)) * (1 + (cdl.gain_master || 0));
    offset[c] = (cdl.offset?.[c] || 0) + (cdl.offset_master || 0)
      + 0.5 * ((cdl.lift?.[c] || 0) + (cdl.lift_master || 0));
    power[c] = 1 / Math.max(0.05, 1 + (cdl.gamma?.[c] || 0) + (cdl.gamma_master || 0));
  }
  const cdlActive = slope.some((s) => Math.abs(s - 1) > 1e-6)
    || offset.some((o) => Math.abs(o) > 1e-6)
    || power.some((p) => Math.abs(p - 1) > 1e-6);
  const adjActive = Math.abs(adj.contrast - 1) > 1e-6
    || Math.abs(adj.saturation - 1) > 1e-6
    || Math.abs(adj.temp) > 1e-6 || Math.abs(adj.tint) > 1e-6;
  const curves = ops.curves || {};
  const curveLuts = {
    master: bakeCurve1D(curves.master), r: bakeCurve1D(curves.r),
    g: bakeCurve1D(curves.g), b: bakeCurve1D(curves.b),
  };
  const curvesActive = !!(curveLuts.master || curveLuts.r || curveLuts.g || curveLuts.b);
  const lutParsed = ops.lut_ref?.url ? lutCache?.[ops.lut_ref.url] || null : null;
  const qual = ops.qualifier?.enabled ? { ...IDENTITY_QUALIFIER(), ...ops.qualifier } : null;
  return {
    slope, offset, power, adj, cdlActive, adjActive,
    curveLuts, curvesActive,
    lut: lutParsed, lutIntensity: ops.lut_ref?.intensity ?? 1,
    qual,
    anyActive: cdlActive || adjActive || curvesActive || !!lutParsed || !!qual,
  };
}

/**
 * Apply the enabled node stack to an ImageData buffer IN PLACE (CPU engine).
 * opts: { matteIdx: render node N's qualifier matte as grayscale,
 *         lutCache: { url → parsed .cube } }
 */
export function applyNodes(imageData, nodes, opts = {}) {
  const { matteIdx = -1, lutCache = {} } = opts;
  const enabled = (nodes || []).filter((n) => n.enabled !== false);
  const compiled = enabled.map((n) => compileNode(n, lutCache));
  if (matteIdx < 0 && !compiled.some((c) => c.anyActive)) return imageData;

  const d = imageData.data;
  const lutOut = [0, 0, 0];
  for (let i = 0; i < d.length; i += 4) {
    let r = d[i] / 255, g = d[i + 1] / 255, b = d[i + 2] / 255;

    if (matteIdx >= 0) {
      // Matte view: show the active node's qualifier mask as grayscale.
      const q = compiled[matteIdx]?.qual;
      const m = q ? qualifierMask(q, r, g, b) : 1;
      d[i] = d[i + 1] = d[i + 2] = m * 255;
      continue;
    }

    for (const n of compiled) {
      if (!n.anyActive) continue;
      const r0 = r, g0 = g, b0 = b; // node input (for qualifier mix)
      if (n.cdlActive) {
        r = Math.pow(clamp01(r * n.slope[0] + n.offset[0]), n.power[0]);
        g = Math.pow(clamp01(g * n.slope[1] + n.offset[1]), n.power[1]);
        b = Math.pow(clamp01(b * n.slope[2] + n.offset[2]), n.power[2]);
      }
      if (n.adjActive) {
        const { contrast, pivot, saturation, temp, tint } = n.adj;
        r = (r - pivot) * contrast + pivot;
        g = (g - pivot) * contrast + pivot;
        b = (b - pivot) * contrast + pivot;
        const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        r = clamp01(luma + (r - luma) * saturation + temp * 0.1);
        g = clamp01(luma + (g - luma) * saturation + tint * 0.1);
        b = clamp01(luma + (b - luma) * saturation - temp * 0.1);
      }
      if (n.curvesActive) {
        const { master, r: cr, g: cg, b: cb } = n.curveLuts;
        if (master) { r = sampleLut1D(master, r); g = sampleLut1D(master, g); b = sampleLut1D(master, b); }
        if (cr) r = sampleLut1D(cr, r);
        if (cg) g = sampleLut1D(cg, g);
        if (cb) b = sampleLut1D(cb, b);
      }
      if (n.lut) {
        sampleLut3D(n.lut, r, g, b, lutOut);
        const t = n.lutIntensity;
        r = clamp01(r * (1 - t) + lutOut[0] * t);
        g = clamp01(g * (1 - t) + lutOut[1] * t);
        b = clamp01(b * (1 - t) + lutOut[2] * t);
      }
      if (n.qual) {
        const m = qualifierMask(n.qual, r0, g0, b0);
        r = r0 + (r - r0) * m;
        g = g0 + (g - g0) * m;
        b = b0 + (b - b0) * m;
      }
    }
    d[i] = r * 255; d[i + 1] = g * 255; d[i + 2] = b * 255;
  }
  return imageData;
}
